import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import type { CustomerFavoriteDetailVo, Page } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { customerFavoriteService } from "@/api/services/customerFavoriteService";

export interface CollectorsTableDialogProps {
    open: boolean;
    productId: string;
    title?: string;
    onClose: () => void;
}

/** 旧接口返回手机号，但共享实体暂未声明该兼容字段。 */
type FavoriteRow = CustomerFavoriteDetailVo & {
    customerFavoriteDetail: CustomerFavoriteDetailVo["customerFavoriteDetail"] & { mobile?: string };
};

/** 对齐旧 Vue 收藏明细弹窗，分页请求指定商品的收藏操作记录。 */
export function CollectorsTableDialog({ open, productId, title, onClose }: CollectorsTableDialogProps) {
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState<FavoriteRow[]>([]);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState<{ key: string; order: "asc" | "desc" }>();

    useEffect(() => {
        if (!open || !productId) return;
        setPage(1);
        setSort(undefined);
    }, [open, productId]);

    useEffect(() => {
        if (!open || !productId) return;
        let disposed = false;
        void customerFavoriteService
            .paging({
                page,
                size: 10,
                productId,
                ...(sort ? { sortKey: sort.key, sortOrder: sort.order } : {})
            })
            .then((result: Page<FavoriteRow>) => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
            });
        return () => {
            disposed = true;
        };
    }, [open, page, productId, sort]);

    const columns: TableColumnProps<FavoriteRow>[] = [
        {
            label: "姓名",
            minWidth: 160,
            prop: "customerFavoriteDetail.userName",
            render: ({ row }) => row.customerFavoriteDetail.userName || "--"
        },
        {
            label: "手机号",
            minWidth: 180,
            prop: "customerFavoriteDetail.mobile",
            render: ({ row }) => row.customerFavoriteDetail.mobile || "--"
        },
        { label: "终端", minWidth: 160, prop: "platform", render: ({ row }) => row.platform || "--" },
        {
            label: "操作",
            minWidth: 180,
            prop: "customerFavoriteDetail.operation",
            render: ({ row }) => (row.customerFavoriteDetail.operation === 1 ? "收藏" : "取消收藏")
        },
        {
            label: "收藏时间",
            minWidth: 180,
            prop: "customerFavoriteDetail.actionTime",
            sortable: "custom",
            render: ({ row }) => row.customerFavoriteDetail.actionTime?.replace("T", " ").slice(0, 16) || "--"
        }
    ];

    return (
        <ZhDialog open={open} title="收藏明细" width={920} footer={null} onCancel={onClose}>
            <ZhTable<FavoriteRow>
                data={rows}
                columns={columns}
                rowKey={row => String(row.customerFavoriteDetail.id)}
                pagination={false}
                isShowSearchBtn={false}
                isShowResetBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <div className="mb-4">
                            {productId}/{title || ""}
                        </div>
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination current={page} pageSize={20} total={total} hideOnSinglePage onChange={setPage} />
                    </div>
                }
                onSortChange={({ prop, order }) => {
                    if (!prop) return;
                    setPage(1);
                    setSort(order ? { key: "action_time", order: order === "ascending" ? "asc" : "desc" } : undefined);
                }}
            />
        </ZhDialog>
    );
}

export default CollectorsTableDialog;
